<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { $router } from '@/router'
import { $bus } from '@/common/events'
import { dialog } from '@/common/dialog'
import { useTitle } from '@/composables/title'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import Barcode from '@/components/Barcode.vue'
import EventLog from '@/components/EventLog.vue'

const instance = getCurrentInstance();

const counters = ref({
  //"hcu/uptime": 3,
  //"hcu/cabinet/drum/status": 5,
})

const groups = ref([
  {
    group: "System",
    topics: [
      { topic: "hcu/uptime/get", name: "Get uptime", direct: true }, //direct: true means trigger directly without input nor prompt
      { topic: "hcu/uptime", sample: { startup: "", uptime: "" } },
      { topic: "hcu/usb/attach", sample: { vid: "vid", pid: "pid" } },
      { topic: "hcu/usb/detach", sample: { vid: "vid", pid: "pid" } },
    ]
  },
  {
    group: "Cabinet",
    topics: [
      { topic: "hcu/cabinet/drum/debug" },
      { topic: "hcu/cabinet/drum/open", name: "Open tray" },
      { topic: "hcu/cabinet/drum/open/response" },
      { topic: "hcu/cabinet/drum/reply" },
      { topic: "hcu/cabinet/drum/status", name: "Get status", direct: true },
      { topic: "hcu/cabinet/drum/position" },
      { topic: "hcu/cabinet/tray/open" },
      { topic: "hcu/cabinet/tray/open/event" },
      { topic: "hcu/cabinet/tray/open/response" },
      { topic: "hcu/cabinet/tray/status/get", name: "Get status" },
      { topic: "hcu/cabinet/tray/status" },
    ]
  },
  {
    group: "Barcode",
    topics: [
      { topic: "hcu/barcode/scan", sample: { code: "code" } },
      { topic: "hcu/barcode/status", sample: { status: "status" } },
      { topic: "hcu/barcode", sample: { code: "code" } },
      { topic: "hcu/barcode/status/get" },
      { topic: "hcu/barcode/status", sample: { status: "barcode.status" } },
      { topic: "hcu/barcode/usb/attach", sample: { name: "config.barcode.name", vid: "vid", pid: "pid" } },
      { topic: "hcu/barcode/usb/detach", sample: { name: "config.barcode.name", vid: "vid", pid: "pid" } },
    ]
  },
  {
    group: "Smart card",
    topics: [
      { topic: "hcu/smartcard/usb/attach", sample: { name: "config.smartcard.name", vid: "vid", pid: "pid" } },
      { topic: "hcu/smartcard/usb/detach", sample: { name: "config.smartcard.name", vid: "vid", pid: "pid" } },
      { topic: "hcu/smartcard/status", sample: { status: "status" } },
      { topic: "hcu/smartcard/device/attach" },
      { topic: "hcu/smartcard/device/detach" },
      { topic: "hcu/smartcard/card/attach", sample: { type: "card.type", uid: "card.uid" } },
      { topic: "hcu/smartcard/card/detach" },
      { topic: "hcu/smartcard/card/read" },
      { topic: "hcu/smartcard/card/write" },
      { topic: "hcu/smartcard/card/auth" },
      { topic: "hcu/smartcard/card/queue/get" },
      { topic: "hcu/smartcard/card/queue", sample: { queue: "smartcard.queue" } },
      { topic: "hcu/smartcard/card/queue/set" },
      { topic: "hcu/smartcard/card/enqueue" },
      { topic: "hcu/smartcard/card/clear" },
      { topic: "hcu/smartcard/status/get" },
      { topic: "hcu/smartcard/status", sample: { status: "smartcard.status" } },
    ]
  },
]);

const responses = ref([
  //{ topic: "hcu/smartcard/card/queue", payload: { queue: "smartcard.queue" } },
  //{ topic: "hcu/smartcard/status", payload: { status: "smartcard.status" } }
]);

const filteredResponses = computed(() => {
  let matchText = topicFilter.value && topicFilter.value.trim().length > 0 ? topicFilter.value.trim() : null;
  if (matchText) {
    return responses.value.filter(x => x.topic && x.topic.includes(matchText));
  }
  return responses.value;
});

const topicRoute = ref("")
const topicPayload = ref("")
const topicSample = ref("")

function openTopic(topic) {
  topicRoute.value = topic.topic;
  topicPayload.value = topic.payload || "";
  topicSample.value = topic.sample ? JSON.stringify(topic.sample, null, 2) : "";
  if (viewMode.value == "log") {
    viewMode.value = "edit";
  }
  if (topic.direct && viewMode.value == "log") {
    publishTopic();
  }
}

let socket = null;

function publishTopic() {
  let data = {
    topic: topicRoute.value,
    payload: topicPayload.value
  };

  if (socket && data.topic && data.topic.trim().length) {
    socket.emit("publish", data);
    /*if (counters.value[data.topic] > 0) {
      counters.value[data.topic]++;
    }
    else {
      counters.value[data.topic] = 1;
    }*/
    //instance?.proxy?.$forceUpdate();
    responses.value.push({ color: "green", ...data });
  }
}

function getTopics() {
  let topics = [ ];
  for (let group of groups.value) {
    for (let topic of group.topics) {
      topics.push({ topic: topic.topic });
    }
  }
  return topics;
}

function clearLogs() {
  responses.value = [ ];
  counters.value = { };
}

onMounted(() => {
  useTitle("MQTT");
  useMenu("admin");
  useSubmenu(false);
  useTopBar(true);

  setTimeout(() => {
    initMQTT();
  }, 100);
});

function initMQTT() {
  socket = io("/ws/v1/mqtt");
  socket.on('connect', () => {
    console.log("Socket:", socket.connected); // true
    responses.value.push({ color: "yellow", topic: "Socket", payload: "connected" });
  });
  socket.on('disconnect', () => {
    console.log("Socket:", socket.connected); // false
    responses.value.push({ color: "red", topic: "Socket", payload: "disconnected" });
  });
  socket.on('received', data => {
    console.log("received", data);
    if (counters.value[data.topic] > 0) {
      counters.value[data.topic]++;
    }
    else {
      counters.value[data.topic] = 1;
    }
    //instance?.proxy?.$forceUpdate();
    responses.value.push({ color: null, topic: data.topic, payload: data.payload });
  });
  socket.emit("subscribe", getTopics());
  console.log("init mqtt done")
}

const viewMode = ref("log");

function showEdit() {
  viewMode.value = "edit";
}

function showLog() {
  viewMode.value = "log";
}

function goBack() {
  try {
    $router.push({ name: "Admin" });
  }
  catch (err) {
    dialog(err);
  }
}

const topicFilter = ref("");
</script>

<template>
  <div class="relative min-h-screen dark:bg-gray-900 dark:text-white p-5">

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between">
      <div class="text-lg font-bold dark:text-white">{{ $t('admin.message_queue_title') }}</div>
      <div class="flex flex-row justify-items-strech space-x-2">
        <Button type="secondary" @click="goBack()">{{ $t('generic.back') }}</Button>
        <Button type="warning" @click="clearLogs()" :enabled="viewMode == 'log'">{{ $t('generic.clear') }}</Button>
        <Button type="secondary" @click="showLog()" v-if="viewMode == 'edit'">{{ $t('admin.log_mode') }}</Button>
        <Button type="secondary" @click="showEdit()" v-if="viewMode == 'log'">{{ $t('admin.publish_mode') }}</Button>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-2 w-full h-modal">
      <div class="h-full">

        
        <div class="w-full h-full grid grid-cols-3 gap-8 mt-4 pb-20">

          <!-- Topics -->
          <div class="w-full">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{ $t('admin.list_of_topics') }}</span>
            </h6>
            <div class="w-full h-full pb-4 mt-2">
              <ul>
                <li v-for="group in groups" class="mt-0.5">
                  <div><i class="fa fa-folder-open text-amber-300"></i><span class="ml-2 text-amber-600">{{ group.group }}</span></div>
                  <ul class="ml-3">
                    <li v-for="topic in group.topics" class="mt-0.5">
                      <i class="fa fa-link text-slate-400"></i>
                      <a href="#" @click.prevent="openTopic(topic)" class="ml-2 text-blue-900 hover:text-blue-700 hover:underline">{{ topic.topic }}</a>
                      <span class="ml-2 text-blue-400" v-if="counters[topic.topic] > 0">({{ counters[topic.topic] }})</span>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>

          <!-- Publish -->
          <div class="col-span-2" v-if="viewMode == 'edit'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{ $t('admin.publish_message') }}</span>
            </h6>
            <div class="w-full h-full pb-4 mt-2">
              <div>
                <Label>{{ $t('admin.topic') }}</Label>
                <div class="flex flex-row gap-2 mt-2">
                  <TextBox :placeholder="$t('admin.topic')" v-model="topicRoute" class="w-full" />
                  <Button @click="publishTopic">{{ $t('admin.publish') }}</Button>
                </div>
              </div>
              <div class="mt-4">
                <Label>{{ $t('admin.payload') }}</Label>
                <TextArea :rows="30" :placeholder="topicSample" v-model="topicPayload" class="h-full mt-2" />
              </div>
            </div>
          </div>

          <!-- Received -->
          <div class="col-span-2" v-if="viewMode == 'log'">
            <h6 class="w-full text-lg font-bold dark:text-white flex justify-between">
              <span>{{ $t('admin.logged_messages') }}</span>
            </h6>
            <div class="w-full mt-2">
                <TextBox :placeholder="$t('admin.filter_by_topic')" v-model="topicFilter" class="w-full" />
            </div>
            <div class="w-full pb-4 mt-2 bg-white border border-gray-300 rounded-lg shadow overflow-x-hidden overflow-y-auto" style="height: 850px;" v-chat-scroll>
              <div v-for="response in filteredResponses" class="w-full mb-1 px-3 py-2" :class="{ 'bg-blue-100 text-blue-800': !response.color, 'bg-green-100 text-green-800': response.color == 'green', 'bg-yellow-100 text-yellow-800': response.color == 'yellow', 'bg-red-100 text-re-800': response.color == 'red' }">
                <div class="font-medium">{{ response.topic }}</div>
                <div>{{ response.payload }}</div>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>

  </div>
</template>

<style scoped>
</style>
